'use client';

import Link from 'next/link';
import Image from 'next/image';
import { motion, Variants } from 'framer-motion';
import { Button } from '@/components/ui/button';

const heroTextContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const heroTextItem: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

/**
 * Hero section - Warm lifestyle image with mother and child
 *
 * Features:
 * - Full-width background image of mother reading with child on sofa
 * - Gradient overlay for text readability on left
 * - Serif headline typography
 * - Terracotta CTA button
 */
export function Hero() {
  return (
    <section className="relative min-h-[50vh] md:min-h-[55vh] flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <Image
          src="/marketing/hero-background.png?v=2"
          alt="Mother reading with child on sofa"
          fill
          className="object-cover object-center"
          priority
          quality={90}
        />
        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-cream-200/95 via-cream-200/80 to-cream-200/40 md:to-transparent" />
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl pt-24 md:pt-32 pb-16 relative z-20">
        <div className="max-w-xl lg:max-w-lg">
          {/* Text Content */}
          <motion.div
            variants={heroTextContainer}
            initial="hidden"
            animate="visible"
          >
            <motion.h1
              variants={heroTextItem}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-[2.75rem] xl:text-[3.25rem] font-serif font-semibold leading-[1.15] text-charcoal-600 mb-5"
            >
              Curate Their Childhood:
              <br />
              <span className="italic">Turn Art into Timeless Tales</span>
            </motion.h1>

            <motion.p
              variants={heroTextItem}
              className="text-base md:text-lg text-taupe-500 leading-relaxed mb-8"
            >
              The elegant way to preserve their drawings as
              <br className="hidden sm:block" />
              narrated stories. Less clutter, more connection.
            </motion.p>

            <motion.div variants={heroTextItem}>
              <Link href="/signup">
                <Button
                  size="lg"
                  className="bg-terracotta-500 hover:bg-terracotta-600 text-white rounded-lg px-8 py-6 text-base font-medium shadow-lg shadow-terracotta-500/20"
                >
                  Create Your First Story
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
